import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./routes/ProtectedRoute";
import { routes } from "./routes";
import NoSubscription from "@components/ui/NoSubscription";
import SplashLoader from "./components/ui/Loaders/SplashLoader";
import { SocketProvider } from "./context/SocketContext";
import MealRecreationManager from "./context/MealRecreationManager";
import SubscribtionSuspended from "@components/ui/SubscribtionSuspended";
import SubscriptionPending from "@components/ui/subscriptionPending";
import { useAuthLoading, useUserData } from "./context/OAuth/AuthContext";
import MedicalViewGuard from "./routes/MedicalViewGuard";
import NetworkGuard from "@routes/NetworkGuard";
import CreatePlansGuard from "@routes/CreatePlansGuard";
import AuthRoutes from "@routes/AuthRoutes";
import AuthLayout from "@layouts/AuthLayout";
import AuthLangRedirect from "@routes/AuthLangRedirect";

const DefaultLayout = lazy(() => import("./layouts/DefaultLayout"));
const Login = lazy(() => import("./pages/login"));
const Logout = lazy(() => import("./pages/logout"));
const ResetPassword = lazy(() => import("@pages/ResetPassword/PasswordReset"));
const PasswordResetConfirm = lazy(
  () => import("@pages/ResetPassword/PasswordResetConfirm"),
);

// Wrap each lazy route component in its own Suspense so navigating between
// pages never triggers the top-level full-screen SplashLoader.
const LazyRoutePage = ({ component: Component, setShowCreatePlan }) => (
  <Suspense fallback={<SplashLoader />}>
    {setShowCreatePlan ? (
      <Component setShowCreatePlan={setShowCreatePlan} />
    ) : (
      <Component />
    )}
  </Suspense>
);

const AppRoutes = () => {
  const { subscription, user, setShowCreatePlan } = useUserData();
  const { isAuthLoading } = useAuthLoading();
  const bypassLayoutPaths = ["/first-forms", "/create-my-plans"];

    // Preload quiz-game chunks in the background after the app is interactive
  useEffect(() => {
    const timer = setTimeout(() => {
      import("@pages/QuizGame/Dashboard");
      import("@features/QuizGame/StartQuizGame");
      import("@features/QuizGame/QuizQuestionares");
      import("@features/QuizGame/ChallengeFinshed");
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const ActiveRoute = ({ children, activeCheck }) => {
    if (isAuthLoading) {
      return <SplashLoader />;
    }

    if (!activeCheck) {
      return children;
    }
    if (!subscription && !isAuthLoading) {
      return (
        <DefaultLayout>
          <NoSubscription />
        </DefaultLayout>
      );
    }

    const { status } = subscription;

    if (status === "pending" || status === "on-hold") {
      return (
        <DefaultLayout>
          <SubscriptionPending />
        </DefaultLayout>
      );
    }

    if (status === "active" || status === "pending-cancel") {
      return children;
    }

    if (status === "cancelled") {
      return (
        <DefaultLayout>
          <SubscribtionSuspended />
        </DefaultLayout>
      );
    }

    return (
      <DefaultLayout>
        <NoSubscription />
      </DefaultLayout>
    );
  };

  const ProtectedRoutesLayout = () => {
    return (
      <SocketProvider user={user}>
        <MealRecreationManager />
        <ProtectedRoute>
          <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <MedicalViewGuard>
                  <CreatePlansGuard>
                    <ActiveRoute activeCheck={route.activeCheck}>
                      {bypassLayoutPaths.includes(route.path) ? (
                        <LazyRoutePage
                          component={route.component}
                          setShowCreatePlan={setShowCreatePlan}
                        />
                      ) : (
                        <DefaultLayout>
                          <LazyRoutePage component={route.component} />
                        </DefaultLayout>
                      )}
                    </ActiveRoute>
                  </CreatePlansGuard>
                </MedicalViewGuard>
              }
            />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ProtectedRoute>
      </SocketProvider>
    );
  };

  return (
    <NetworkGuard>
        <Suspense fallback={<SplashLoader />}>
          <Routes>
            <Route element={<AuthRoutes />}>
              <Route element={<AuthLayout />}>
                <Route
                  path="/login"
                  element={<AuthLangRedirect basePath="/login" />}
                />
                <Route path="/login/:lang" element={<Login />} />
                <Route
                  path="/password-reset"
                  element={<AuthLangRedirect basePath="/password-reset" />}
                />
                <Route
                  path="/password-reset/:lang"
                  element={<ResetPassword />}
                />
                <Route
                  path="/reset-password"
                  element={<AuthLangRedirect basePath="/reset-password" />}
                />
                <Route
                  path="/reset-password/:lang"
                  element={<PasswordResetConfirm />}
                />
              </Route>
            </Route>

            <Route path="/logout" element={<Logout />} />
            <Route path="/*" element={<ProtectedRoutesLayout />} />
          </Routes>
        </Suspense>
    </NetworkGuard>
  );
};

export default AppRoutes;
